import { useState } from 'react'
import { Offer } from 'src/types/offer'
import {
  ELECTRONIC_GIFT_CARD_MIN,
  ELECTRONIC_GIFT_CARD_MAX,
} from 'shared/constants'
import { TextLink, StoreIcon, PriceTag } from 'baby-design'
import { Plus, Minus } from 'baby-design/icons'
import css from './BuyingOptions.styles.scss'
import {
  formatDisplayPrice,
  getPriceTagPropsFromPriceDetails,
} from '../../../../utils/productHelpers'

interface BuyingOptionProps {
  option: Offer
  onBuyingOptionClick: (offer: Offer) => void
  isGiftCard: boolean
  inSaleUIFeature?: boolean
}

const LegacyPriceTag = ({
  price,
  isGiftCard,
}: {
  price: string | number | undefined
  isGiftCard: boolean
}) => (
  <div className={css.buying_option__price}>
    {isGiftCard ? '$25 – $1,000' : formatDisplayPrice(price)}
  </div>
)

const BuyingOption = ({
  option,
  onBuyingOptionClick,
  isGiftCard,
  inSaleUIFeature,
}: BuyingOptionProps) => {
  let priceTagProps = {}
  if (isGiftCard) {
    priceTagProps = {
      minPrice: ELECTRONIC_GIFT_CARD_MIN,
      maxPrice: ELECTRONIC_GIFT_CARD_MAX,
      truncateZeroCents: true,
    }
  } else if (option.priceDetails) {
    const { msrp, minPrice, maxPrice, currentPrice, onSale, saleCallouts } =
      getPriceTagPropsFromPriceDetails(option.priceDetails)
    priceTagProps = {
      callouts: saleCallouts,
      currentPrice,
      currentlyInActiveSale: onSale,
      minPrice,
      maxPrice,
      msrp,
    }
  }

  return (
    <a
      className={css.buying_option}
      onClick={() => onBuyingOptionClick(option)}
    >
      <div className={css.buying_option__info}>
        <StoreIcon
          className={css.buying_option__icon}
          store={{ name: option.storeIconName }}
        />
        <div className={css.buying_option__name}>{option.storeDisplayName}</div>
      </div>
      <div className={css.buying_option__price}>
        {inSaleUIFeature && option.priceDetails ? (
          <PriceTag {...priceTagProps} size="xs" variant="compact" />
        ) : (
          <LegacyPriceTag isGiftCard={isGiftCard} price={option.price} />
        )}
      </div>
    </a>
  )
}

interface BuyingOptionsProps {
  options?: Array<Offer>
  onBuyingOptionClick: (offer: Offer) => void
  isGiftCard: boolean
  inSaleUIFeature?: boolean
}

export const BuyingOptions = ({
  options = [],
  onBuyingOptionClick,
  isGiftCard,
  inSaleUIFeature,
}: BuyingOptionsProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const numMinimizedOptionsToShow = 3
  const slicedOptions = isExpanded
    ? options
    : options.slice(0, numMinimizedOptionsToShow)
  const isOptionsSliced = options.length > numMinimizedOptionsToShow

  return (
    <div className={css.buying_options}>
      <div className={css.buying_options__heading}>Buying Options</div>
      {slicedOptions.map((option) => (
        <BuyingOption
          inSaleUIFeature={inSaleUIFeature}
          isGiftCard={isGiftCard}
          key={option.url}
          option={option}
          onBuyingOptionClick={onBuyingOptionClick}
        />
      ))}
      {isOptionsSliced && (
        <TextLink
          className={css.buying_options__control}
          icon={{ side: 'left', icon: isExpanded ? <Minus /> : <Plus /> }}
          size="sm"
          variant="default"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Show Less' : 'Show More'}
        </TextLink>
      )}
    </div>
  )
}
